import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ChevronRight, UserPlus, Archive, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [boats, setBoats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    boat_id: "",
    customer_name: "",
    customer_email: "",
    start_date: "",
    end_date: "",
    num_guests: 2,
    notes: "",
  });
  const [backfillBusy, setBackfillBusy] = useState(false);

  const load = async () => {
    const [b, bs] = await Promise.all([api.get("/bookings"), api.get("/boats")]);
    setBookings(b.data);
    setBoats(bs.data);
  };

  useEffect(() => { load(); }, []);

  const onCreate = async () => {
    try {
      const { data } = await api.post("/bookings", form);
      if (data.auto_credentials) {
        toast.success(
          `Booking opprettet + kundeprofil. Passord: ${data.auto_credentials.password}`,
          { duration: 9000 },
        );
      } else {
        toast.success("Booking opprettet");
      }
      setOpen(false);
      setForm({ boat_id: "", customer_name: "", customer_email: "", start_date: "", end_date: "", num_guests: 2, notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opprette booking");
    }
  };

  const backfillProfiles = async () => {
    if (!window.confirm(
      "Gå gjennom alle bookinger som mangler kundeprofil og opprette dem? " +
      "Bookinger uten e-postadresse hoppes over.",
    )) return;
    setBackfillBusy(true);
    try {
      const r = await api.post("/bookings/backfill-profiles");
      const { created_count, linked_count, skipped_count } = r.data;
      toast.success(
        `Opprettet ${created_count} nye profiler, koblet ${linked_count} eksisterende. ` +
        `${skipped_count > 0 ? `${skipped_count} hoppet over (mangler e-post).` : ""}`,
        { duration: 9000 },
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke kjøre");
    } finally {
      setBackfillBusy(false);
    }
  };

  const missingProfiles = bookings.filter((b) => !b.customer_user_id).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Kalender</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Bookinger</h1>
          <p className="text-sm text-[#2B3F4D] mt-1">
            iCal-feed kobles til per båt senere. Inntil videre registreres bookinger manuelt.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/archive?tab=bookings">
            <Button
              variant="outline"
              data-testid="bookings-archive-link"
              className="border-[#0C1824] text-[#0C1824]"
            >
              <Archive className="w-4 h-4 mr-1.5" />
              Se fullførte i arkiv
            </Button>
          </Link>
          {missingProfiles > 0 && (
            <Button
              variant="outline"
              onClick={backfillProfiles}
              disabled={backfillBusy}
              data-testid="bookings-backfill-profiles"
              className="border-[#BD984D] text-[#A57F39] hover:bg-[#BD984D]/10"
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Opprett {missingProfiles} manglende profil{missingProfiles === 1 ? "" : "er"}
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="booking-new" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">
              + Ny booking
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ny booking</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Båt</Label>
                <Select value={form.boat_id} onValueChange={(v) => setForm({ ...form, boat_id: v })}>
                  <SelectTrigger data-testid="booking-boat"><SelectValue placeholder="Velg båt" /></SelectTrigger>
                  <SelectContent>
                    {boats.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kundenavn</Label>
                <Input data-testid="booking-customer" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div>
                <Label>Kunde-epost (valgfri)</Label>
                <Input type="email" data-testid="booking-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Fra</Label>
                  <Input type="date" data-testid="booking-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <Label>Til</Label>
                  <Input type="date" data-testid="booking-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Antall personer</Label>
                <Input type="number" min={1} data-testid="booking-guests" value={form.num_guests} onChange={(e) => setForm({ ...form, num_guests: parseInt(e.target.value, 10) || 0 })} />
              </div>
              <div>
                <Label>Notat</Label>
                <Textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={onCreate} data-testid="booking-create" className="bg-[#BD984D] hover:bg-[#A57F39] text-white">Opprett</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="space-y-3">
        {bookings.length === 0 && <Card className="p-6 text-sm text-[#2B3F4D]">Ingen bookinger registrert.</Card>}
        {[...bookings].sort((a, b) => {
          // Sortér etter nærhet til i dag: aktive bookinger først, deretter
          // kommende (nærmeste øverst), til slutt tidligere (nyest først).
          const today = new Date().toISOString().slice(0, 10);
          const bucket = (bk) => {
            const s = (bk.start_date || "").slice(0, 10);
            const e = (bk.end_date || "").slice(0, 10);
            if (s && e && s <= today && today <= e) return 0; // aktiv
            if (s && s > today) return 1; // kommende
            return 2; // tidligere
          };
          const ba = bucket(a);
          const bb = bucket(b);
          if (ba !== bb) return ba - bb;
          const sa = (a.start_date || "").slice(0, 10);
          const sb = (b.start_date || "").slice(0, 10);
          if (ba === 2) return sb.localeCompare(sa);
          return sa.localeCompare(sb);
        }).map((b) => {
          const boat = boats.find((x) => x.id === b.boat_id);
          const today = new Date().toISOString().slice(0, 10);
          return (
            <Link key={b.id} to={`/admin/bookings/${b.id}`} className="block"
                  data-testid={`booking-row-${b.id}`}>
              <Card className="p-4 hover:border-[#BD984D]/60 hover:shadow-sm transition-all cursor-pointer">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
                      {boat?.name || "Båt slettet"}
                    </div>
                    <div className="font-semibold text-[#0C1824]">{b.customer_name}</div>
                    <div className="text-xs text-[#2B3F4D] mt-1">
                      {b.start_date?.slice(0, 10)} → {b.end_date?.slice(0, 10)} · {b.num_guests} pers.
                    </div>
                    {b.notes && <div className="text-sm text-[#2B3F4D] mt-2 line-clamp-2">{b.notes}</div>}
                    {b.internal_notes && b.internal_notes.length > 0 && (
                      <div className="text-[11px] text-[#A57F39] mt-1.5 font-semibold">
                        {b.internal_notes.length} interne notat{b.internal_notes.length === 1 ? "" : "er"}
                      </div>
                    )}
                    {(() => {
                      const endIso = (b.end_date || "").slice(0, 10);
                      const isEnded = endIso && endIso < today;
                      if (isEnded && b.has_receipt === false) {
                        return (
                          <div
                            data-testid={`booking-missing-receipt-${b.id}`}
                            className="inline-flex items-center gap-1 mt-2 rounded-full bg-[#A57F39] text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 shadow-sm"
                          >
                            <AlertCircle className="h-3 w-3" /> Mangler kvittering
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
